/**
 * Admin routes — internal admin tools, not accessible to regular users.
 * Requires JWT + role=admin.
 *
 * All admin pages are code-split via React.lazy() so public visitors
 * never download this ~400KB of admin-only code. Each route is its own
 * chunk and only fetched when the user navigates into /admin.
 */
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import { ProtectedRoute } from "../components/common";

// Shell is always needed once you're inside /admin, so import eagerly.
import AdminShell from "../pages/admin";

// All page components lazy-loaded
const AdminDashboard         = lazy(() => import("../pages/admin/dashboard"));
const AdminBusinesses        = lazy(() => import("../pages/admin/businesses"));
const AdminUsers             = lazy(() => import("../pages/admin/users"));
const AdminCategories        = lazy(() => import("../pages/admin/categories"));
const AdminImportHub         = lazy(() => import("../pages/admin/import"));
const AdminMailer            = lazy(() => import("../pages/admin/mailer"));
const AdminBusinessAnalytics = lazy(() => import("../pages/admin/BusinessAnalyticsPage"));
const AdminReviews           = lazy(() => import("../pages/admin/ReviewsPage"));
const DeveloperDashboard     = lazy(() => import("../pages/admin/DeveloperDashboard"));
const AuditLogsPage          = lazy(() => import("../pages/admin/AuditLogsPage"));
const AdminNotificationsPage = lazy(() => import("../pages/admin/AdminNotificationsPage"));
const AdminSubscriptionsPage = lazy(() => import("../pages/admin/AdminSubscriptionsPage"));
const AdminSettingsPage      = lazy(() => import("../pages/admin/AdminSettingsPage"));
const SeoAuditPage           = lazy(() => import("../pages/admin/SeoAuditPage"));
const AdminBlogHome          = lazy(() => import("../pages/admin/blog"));
const BlogDashboard          = lazy(() => import("../pages/admin/blog/BlogDashboard"));
const BlogPostEditor         = lazy(() => import("../pages/admin/blog/PostEditor"));
const BlogCategoriesPage     = lazy(() => import("../pages/admin/blog/CategoriesPage"));
const BlogTagsPage           = lazy(() => import("../pages/admin/blog/TagsPage"));
const BlogAuthorsPage        = lazy(() => import("../pages/admin/blog/AuthorsPage"));
const BlogRedirectsPage      = lazy(() => import("../pages/admin/blog/RedirectsPage"));
const BlogAnalyticsPage      = lazy(() => import("../pages/admin/blog/AnalyticsPage"));

// Google Import V2 module — already a separate dynamic chunk
const GoogleImportSearch       = lazy(() => import("../modules/google-import-v2").then(m => ({ default: m.SearchPage })));
const GoogleImportDrafts       = lazy(() => import("../modules/google-import-v2").then(m => ({ default: m.DraftsListPage })));
const GoogleImportDraftEditor  = lazy(() => import("../modules/google-import-v2").then(m => ({ default: m.DraftEditorPage })));

/** Suspense fallback shown while a chunk is downloading. */
function ChunkLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

const lazyRoute = (Element) => (
  <Suspense fallback={<ChunkLoader />}>
    <Element />
  </Suspense>
);

const AdminRoutes = () => {
  return (
    <Route
      path="admin"
      element={
        <ProtectedRoute requiredRole="admin" requireOnboarding={false}>
          <AdminShell />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard"           element={lazyRoute(AdminDashboard)} />
      <Route path="import"              element={lazyRoute(AdminImportHub)} />
      <Route path="google-import" element={<Outlet />}>
        <Route index                    element={lazyRoute(GoogleImportSearch)} />
        <Route path="drafts"            element={lazyRoute(GoogleImportDrafts)} />
        <Route path="drafts/:id"        element={lazyRoute(GoogleImportDraftEditor)} />
      </Route>
      <Route path="categories"          element={lazyRoute(AdminCategories)} />
      <Route path="businesses"          element={lazyRoute(AdminBusinesses)} />
      <Route path="users"               element={lazyRoute(AdminUsers)} />
      <Route path="mailer"              element={lazyRoute(AdminMailer)} />
      <Route path="business-analytics"  element={lazyRoute(AdminBusinessAnalytics)} />
      <Route path="reviews"             element={lazyRoute(AdminReviews)} />
      <Route path="developer"           element={lazyRoute(DeveloperDashboard)} />
      <Route path="audit"               element={lazyRoute(AuditLogsPage)} />
      <Route path="notifications"       element={lazyRoute(AdminNotificationsPage)} />
      <Route path="subscriptions"       element={lazyRoute(AdminSubscriptionsPage)} />
      <Route path="settings"            element={lazyRoute(AdminSettingsPage)} />
      <Route path="seo"                 element={lazyRoute(SeoAuditPage)} />
      {/* Nested blog routes — avoids RR7 sibling path edge cases; parent only renders <Outlet /> */}
      <Route path="blog" element={<Outlet />}>
        <Route index                    element={lazyRoute(AdminBlogHome)} />
        <Route path="dashboard"         element={lazyRoute(BlogDashboard)} />
        <Route path="new"               element={lazyRoute(BlogPostEditor)} />
        <Route path="edit/:id"          element={lazyRoute(BlogPostEditor)} />
        <Route path="categories"        element={lazyRoute(BlogCategoriesPage)} />
        <Route path="tags"              element={lazyRoute(BlogTagsPage)} />
        <Route path="authors"           element={lazyRoute(BlogAuthorsPage)} />
        <Route path="redirects"         element={lazyRoute(BlogRedirectsPage)} />
        <Route path="analytics"         element={lazyRoute(BlogAnalyticsPage)} />
      </Route>
    </Route>
  );
};

export default AdminRoutes;
